import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { SearchCollectionName, SearchResult, search } from 'api/search';
import { AxiosError } from 'axios';
import { DEFAULT_MULTI_SEARCH_LIMIT } from 'components/search/constants';
import { transformSearchResults } from 'components/search/transform';
import { ErrorCodes } from 'constants/error-codes';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { searchQueryKeys } from 'queries/search-query-keys';

type TransformedSearchResults = ReturnType<typeof transformSearchResults>;

interface UseSearchResultsProps {
    query: string;
    resourceFilter?: SearchCollectionName[];
    limit?: number;
}

const useSearch = (props: UseSearchResultsProps) => {
    const { query, resourceFilter = [], limit = DEFAULT_MULTI_SEARCH_LIMIT } = props;
    const search = useSearchApi();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const lastResultsCache = useRef<TransformedSearchResults>();

    const queryResults = useSuspenseQuery({
        queryKey: searchQueryKeys.search(accountId, {
            query,
            resourceFilter,
            limit,
        }),
        queryFn: async () => {
            if (query === '') {
                return { data: [], shouldTransform: true };
            }

            try {
                const data = await search(query, resourceFilter);

                return { data, shouldTransform: true };
            } catch (e) {
                if ((e as AxiosError).response?.status === ErrorCodes.UNAUTHORIZED) {
                    await queryClient.invalidateQueries({
                        queryKey: accountQueryKeys.current(accountId),
                        exact: true,
                    });
                    return {
                        data: lastResultsCache.current
                            ? (lastResultsCache.current as unknown as SearchResult[])
                            : [],
                        shouldTransform: !lastResultsCache.current,
                    };
                } else {
                    throw e;
                }
            }
        },
        select: ({ data, shouldTransform }) =>
            shouldTransform
                ? transformSearchResults(data)
                : (data as unknown as TransformedSearchResults),
        staleTime: 0,
        retry: false,
    });

    useEffect(() => {
        lastResultsCache.current = queryResults.data;
    }, [queryResults.data]);

    return queryResults;
};

const useSearchApi = (limit = DEFAULT_MULTI_SEARCH_LIMIT) => {
    const { search_api_url, search_api_key, searchAllowedResources, searchCollections } =
        useCurrentAccount();

    return useMemo(
        () =>
            search(
                search_api_url as string,
                search_api_key as string,
                searchAllowedResources,
                searchCollections,
                limit,
            ),
        [search_api_url, search_api_key, searchAllowedResources, searchCollections, limit],
    );
};

export { useSearch, useSearchApi };
